import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useTranslation } from 'react-i18next';

export type Region = 'TR' | 'US' | 'UK';
export type Language = 'tr-TR' | 'en-US' | 'en-GB';
export type Currency = 'TRY' | 'USD' | 'GBP';

interface RegionConfig {
    region: Region;
    language: Language;
    currency: Currency;
    currencySymbol: string;
    shippingCarriers: string[];
    paymentProviders: string[];
    theme: {
        landmark: string;
        vibe: string;
        motifIcon: string;
    }
}

const REGION_CONFIGS: Record<Region, RegionConfig> = {
    TR: {
        region: 'TR',
        language: 'tr-TR',
        currency: 'TRY',
        currencySymbol: '₺',
        shippingCarriers: ['Yurtiçi Kargo', 'Aras Kargo', 'MNG Kargo', 'Sendeo', 'PTT Kargo'],
        paymentProviders: ['iyzico', 'PayTR'],
        theme: {
            landmark: 'PREMIUM',
            vibe: 'ULTIMATE',
            motifIcon: '💎'
        }
    },
    US: {
        region: 'US',
        language: 'en-US',
        currency: 'USD',
        currencySymbol: '$',
        shippingCarriers: ['USPS', 'FedEx', 'UPS'],
        paymentProviders: ['Stripe', 'PayPal'],
        theme: {
            landmark: 'PREMIUM',
            vibe: 'ULTIMATE',
            motifIcon: '💎'
        }
    },
    UK: {
        region: 'UK',
        language: 'en-GB',
        currency: 'GBP',
        currencySymbol: '£',
        shippingCarriers: ['Royal Mail', 'DPD', 'Hermes', 'Yodel'],
        paymentProviders: ['Stripe', 'PayPal'],
        theme: {
            landmark: 'PREMIUM',
            vibe: 'ULTIMATE',
            motifIcon: '💎'
        }
    }
};

interface RegionContextType {
    region: Region;
    config: RegionConfig;
    setRegion: (region: Region) => Promise<void>;
    isRegionLocked: boolean;
    needsSelection: boolean;
    formatCurrency: (amount: number, options?: Intl.NumberFormatOptions) => string;
    formatDate: (timestamp: number, format?: 'short' | 'long') => string;
}

const RegionContext = createContext<RegionContextType | undefined>(undefined);

export const RegionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentUser } = useAuth();
    const { i18n } = useTranslation();
    const [region, setRegionState] = useState<Region>('TR');
    const [isRegionLocked, setIsRegionLocked] = useState(false);
    const [needsSelection, setNeedsSelection] = useState(false);

    // Initialize region from user preferences or auto-detect
    useEffect(() => {
        const initializeRegion = async () => {
            if (currentUser?.preferredRegion) {
                // User has a saved region preference
                setRegionState(currentUser.preferredRegion as Region);
                setIsRegionLocked(!!currentUser.regionLockedAt);

                // Sync language with region
                const lang = REGION_CONFIGS[currentUser.preferredRegion as Region].language;
                if (i18n.language !== lang) {
                    await i18n.changeLanguage(lang);
                }
                localStorage.setItem('fct_language', lang); // Cross-tab/session sync
            } else {
                // Auto-detect region from IP (fallback to TR)
                const detected = await detectRegionFromIP();

                // Check local storage for previous session selection
                const hasSelected = localStorage.getItem('fct_region_selected') === 'true';
                const persistedLang = localStorage.getItem('fct_language');

                if (hasSelected && persistedLang) {
                    // Session exists, use persisted values
                    setRegionState(detected);
                    if (i18n.language !== persistedLang) {
                        await i18n.changeLanguage(persistedLang);
                    }
                } else if (!hasSelected) {
                    // New user or no selection - Force onboarding
                    setNeedsSelection(true);
                    setRegionState(detected);
                    // Default to English onboarding
                    if (i18n.language !== 'en-US') {
                        await i18n.changeLanguage('en-US');
                    }
                } else {
                    // Selection marked but no language stored? Fallback to detected region's default
                    setRegionState(detected);
                    const lang = REGION_CONFIGS[detected].language;
                    if (i18n.language !== lang) {
                        await i18n.changeLanguage(lang);
                    }
                    localStorage.setItem('fct_language', lang);
                }
            }
        };

        initializeRegion();
    }, [currentUser, i18n]);

    const setRegion = async (newRegion: Region) => {
        // Enforce region lock
        if (isRegionLocked && currentUser) {
            throw new Error('Region is locked. Contact support to change your region.');
        }

        setRegionState(newRegion);

        // Change language to match region
        const newLang = REGION_CONFIGS[newRegion].language;
        await i18n.changeLanguage(newLang);
        localStorage.setItem('fct_language', newLang); // Sync with i18next detector

        // Save to Firestore if user is logged in
        if (currentUser) {
            await updateDoc(doc(db, 'users', currentUser.id), {
                preferredRegion: newRegion,
                preferredLanguage: REGION_CONFIGS[newRegion].language,
                preferredCurrency: REGION_CONFIGS[newRegion].currency,
                regionLockedAt: Date.now() // Lock region after first selection
            });
            setIsRegionLocked(true);
        }

        setNeedsSelection(false);
    };

    const formatCurrency = (amount: number, options?: Intl.NumberFormatOptions): string => {
        const config = REGION_CONFIGS[region];
        return new Intl.NumberFormat(config.language, {
            style: 'currency',
            currency: config.currency,
            ...options
        }).format(amount);
    };

    const formatDate = (timestamp: number, format: 'short' | 'long' = 'short'): string => {
        const config = REGION_CONFIGS[region];
        const options: Intl.DateTimeFormatOptions = format === 'long'
            ? { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }
            : { year: 'numeric', month: 'short', day: 'numeric' };

        return new Intl.DateTimeFormat(config.language, options).format(timestamp);
    };

    return (
        <RegionContext.Provider value={{
            region,
            config: REGION_CONFIGS[region],
            setRegion,
            isRegionLocked,
            needsSelection,
            formatCurrency,
            formatDate
        }}>
            {children}
        </RegionContext.Provider>
    );
};

export const useRegion = () => {
    const context = useContext(RegionContext);
    if (!context) {
        throw new Error('useRegion must be used within RegionProvider');
    }
    return context;
};

/**
 * Auto-detect region from IP address
 * Uses ipapi.co free tier (1000 requests/day)
 * Fallback to TR if detection fails
 */
async function detectRegionFromIP(): Promise<Region> {
    try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();

        const countryCode = data.country_code;

        if (countryCode === 'TR') return 'TR';
        if (countryCode === 'US') return 'US';
        if (countryCode === 'GB') return 'UK';

        // Default to TR for other countries
        return 'TR';
    } catch (error) {
        console.warn('Failed to detect region from IP, defaulting to TR:', error);
        return 'TR';
    }
}
